import { memo, useMemo } from "react";
import { PushPin, X } from "@/components/ui/icons";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { AnimatedImage } from "@/components/ui/animated-image";

type UserInfo = {
  display_name: string;
  avatar_url?: string | null;
};

interface PinnedMessagesPanelProps {
  pins: readonly { id: string; message_id: string }[];
  messageById: ReadonlyMap<
    string,
    {
      id: string;
      user_id: string;
      content: string;
      created_at: number;
      sender?: UserInfo | null;
    }
  >;
  userMap: ReadonlyMap<string, UserInfo>;
  onClose: () => void;
  onScrollToMessage: (messageId: string) => void;
}

export const PinnedMessagesPanel = memo(function PinnedMessagesPanel({
  pins,
  messageById,
  userMap,
  onClose,
  onScrollToMessage,
}: PinnedMessagesPanelProps) {
  // Derive count and list from the same source — pins whose message is
  // actually loaded. Orphan pins (message deleted/not yet loaded) must not
  // inflate the counter while rendering nothing.
  const visiblePins = useMemo(
    () => pins.filter((pin) => messageById.has(pin.message_id)),
    [pins, messageById],
  );
  return (
    <div className="w-72 flex flex-col bg-background relative">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border/95 to-transparent" />
      <div className="h-[38px] shrink-0" />
      <div className="h-12 px-4 flex items-center justify-between relative">
        <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-border to-transparent" />
        <div className="flex items-center gap-2">
          <PushPin size={16} weight="Bold" className="text-warning" />
          <span className="font-semibold text-sm">
            Pinned Messages ({visiblePins.length})
          </span>
        </div>
        <ToolbarIconButton
          onClick={onClose}
          className="h-8 w-8"
          aria-label="Close pinned messages"
        >
          <X size={14} className="text-muted-foreground" />
        </ToolbarIconButton>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-2">
        {visiblePins.length === 0 && (
          <p className="text-muted-foreground/50 text-xs px-2 py-2">
            No pinned messages in this channel.
          </p>
        )}
        {visiblePins.map((pin) => {
          const msg = messageById.get(pin.message_id);
          if (!msg) return null;
          const msgUser = msg.sender ?? userMap.get(msg.user_id);
          return (
            <div
              key={pin.id}
              className="p-2 rounded-lg bg-foreground/[0.03] hover:bg-foreground/[0.06] cursor-pointer"
              onClick={() => onScrollToMessage(msg.id)}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="h-5 w-5 rounded-full bg-foreground/20 flex items-center justify-center text-[8px] font-semibold overflow-hidden">
                  {msgUser?.avatar_url ? (
                    <AnimatedImage
                      src={msgUser.avatar_url}
                      alt={msgUser.display_name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    (msgUser?.display_name || "?").slice(0, 2).toUpperCase()
                  )}
                </div>
                <span className="text-xs font-semibold">
                  {msgUser?.display_name || "Unknown"}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {new Date(msg.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-xs text-foreground/70 line-clamp-2">
                {msg.content}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
});
